import { Component, inject } from '@angular/core';
import { Router, RouterLink, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { LucideAngularModule } from 'lucide-angular';
import { AuthStore } from '@/store/auth.store';
import { SidebarUiStore } from '@/store/sidebar-ui.store';
import { BreakpointService } from '@/services/breakpoint/breakpoint.service';
import { resolvePageTitle } from '@/routes/path-titles';
import { APP_ROUTES } from '@/routes/routes.type';
import { AppButtonComponent } from '@/components/button';

@Component({
  selector: '.view-header',
  templateUrl: './header.component.html',
  imports: [RouterLink, LucideAngularModule, AppButtonComponent],
})
export class HeaderComponent {
  readonly #router = inject(Router);
  readonly authStore = inject(AuthStore);
  readonly sidebarUiStore = inject(SidebarUiStore);
  readonly breakpointService = inject(BreakpointService);

  readonly loginPath = '/' + APP_ROUTES.LOGIN;
  readonly registerPath = '/' + APP_ROUTES.REGISTER;

  readonly pageTitle = toSignal(
    this.#router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.#resolvePageTitle()),
    ),
    { initialValue: this.#resolvePageTitle() as string | null },
  );

  readonly isBlogContext = toSignal(
    this.#router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.#isBlogUrl()),
      startWith(this.#isBlogUrl()),
    ),
    { initialValue: this.#isBlogUrl() },
  );

  readonly isLandingContext = toSignal(
    this.#router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.#isLandingUrl()),
      startWith(this.#isLandingUrl()),
    ),
    { initialValue: this.#isLandingUrl() },
  );

  toggleSidebar(): void {
    if (this.breakpointService.deviceType() !== 'desktop') {
      this.sidebarUiStore.toggle();
    }
  }

  /** Путь без query и hash: `router.url` после anchor-навигации — `/#features`. */
  #currentPath(): string {
    const withoutQuery = this.#router.url.split('?')[0];
    const hashIndex = withoutQuery.indexOf('#');
    const path = hashIndex === -1 ? withoutQuery : withoutQuery.slice(0, hashIndex);
    return path === '' ? '/' : path;
  }

  #resolvePageTitle(): string | null {
    const title = resolvePageTitle(this.#currentPath());
    return title === 'Главная' ? null : title;
  }

  #isBlogUrl(): boolean {
    const path = this.#currentPath();
    return path === `/${APP_ROUTES.BLOG}` || path.startsWith(`/${APP_ROUTES.BLOG}/`);
  }

  #isLandingUrl(): boolean {
    return this.#currentPath() === '/';
  }
}
